import { useState, useCallback, useEffect, useRef } from 'react';
import { useStreamingChat } from './useStreamingChat';
import { useChatHistoryStorage, ChatHistoryMessage, ChatHistoryData } from './useChatHistoryStorage';

/**
 * 增强的聊天Hook
 * 结合流式聊天功能和历史记录保存
 * 提供与原frontend JavaScript版本相同的功能体验
 */

export interface EnhancedChatState {
  // 当前聊天状态
  currentChatId: string | null;
  messages: ChatHistoryMessage[];
  conversation: {
    conversationId: string | null;
    sessionId: string | null;
    state: string;
    stage: string;
    caseInfo: Record<string, any>;
    completenessScore: number;
  };
  
  // 聊天历史
  chatList: ChatHistoryData[];
  
  // 加载状态
  isLoading: boolean;
  isStreaming: boolean;
  isSyncing: boolean;
  
  // 其他状态
  currentStatus: string;
  error: string | null;
  lastSyncTime: number | null;
  // 非致命的同步错误（不在聊天界面弹红条）
  syncError?: string | null;
}

export const useEnhancedChat = () => {
  // 使用现有的流式聊天Hook
  const streamingChat = useStreamingChat();
  
  // 使用聊天历史存储Hook
  const chatStorage = useChatHistoryStorage();
  
  // 内部状态追踪
  const [currentLoadedChatId, setCurrentLoadedChatId] = useState<string | null>(null);

  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  /**
   * 转换流式聊天消息为历史消息格式
   */
  const convertStreamingMessageToHistory = useCallback((streamingMessage: any): ChatHistoryMessage => {
    // 安全检查：确保streamingMessage不为null且有必要的属性
    if (!streamingMessage || !streamingMessage.id) {
      // 返回一个默认的消息对象，避免错误
      return {
        id: `fallback-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        role: 'assistant',
        content: '消息加载中...',
        timestamp: Date.now(),
        type: 'normal',
        isStreaming: false,
        isStreamingComplete: true,
      };
    }
    
    return {
      id: streamingMessage.id,
      role: streamingMessage.role || 'assistant',
      content: streamingMessage.content || '',
      timestamp: streamingMessage.timestamp || Date.now(),
      type: streamingMessage.type || 'normal',
      caseInfo: streamingMessage.caseInfo || {},
      analysisCapability: streamingMessage.analysisCapability || '',
      isStreaming: streamingMessage.isStreaming || false,
      isStreamingComplete: streamingMessage.isStreamingComplete || false,
      processingTime: streamingMessage.processingTime || 0,
      files: streamingMessage.files || [],
      // 策略选择相关属性
      strategies: streamingMessage.strategies || [],
      currentStrategy: streamingMessage.currentStrategy || undefined,
      canModify: streamingMessage.canModify || false,
    };
  }, []);

  /**
   * 防抖保存到历史记录
   */
  const debouncedSaveToHistory = useCallback(() => {
    // 如果正在加载聊天，跳过保存
    if (isLoadingChatRef.current) {
      return;
    }

    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    saveTimeoutRef.current = setTimeout(() => {
      if (chatStorage.currentChatId && streamingChat.messages.length > 0) {
        const historyMessages = streamingChat.messages
          .filter(msg => msg) // 过滤掉null/undefined消息
          .map(convertStreamingMessageToHistory);
        
        chatStorage.updateChatData(
          chatStorage.currentChatId,
          {
            messages: historyMessages,
            conversationState: {
              conversationId: streamingChat.conversation.conversationId,
              sessionId: streamingChat.conversation.conversationId, // 使用conversationId作为sessionId
              state: streamingChat.conversation.state,
              stage: 'free', // 默认阶段
              caseInfo: streamingChat.conversation.caseInfo,
              completenessScore: streamingChat.conversation.completenessScore,
            }
          },
          false // 不立即同步，让防抖机制处理
        );
      }
    }, 1000); // 1秒防抖延迟
  }, [chatStorage, streamingChat, convertStreamingMessageToHistory]);

  /**
   * 创建新聊天（带防重复保护）
   */
  const newChatRef = useRef(false);
  const newChat = useCallback(() => {
    // 防止快速重复调用
    if (newChatRef.current) {
      return;
    }
    
    newChatRef.current = true;
    
    try {
      // 创建新的聊天历史记录
      const newChatData = chatStorage.createNewChat();
      
      // 重置流式聊天状态
      streamingChat.newChat();
      
    } finally {
      // 延迟重置防护标志，避免正常的快速操作被阻止
      setTimeout(() => {
        newChatRef.current = false;
      }, 500); // 500ms防护期
    }
  }, [chatStorage, streamingChat]);

  /**
   * 发送消息
   */
  const sendMessage = useCallback(async (content: string, files?: File[]) => {
    try {
      // 确保有当前聊天
      if (!chatStorage.currentChatId) {
        newChat();
      }

      // 发送消息到流式聊天
      await streamingChat.sendMessage(content, files);
      
      // 触发保存（防抖）
      debouncedSaveToHistory();
      
    } catch (error) {
      throw error;
    }
  }, [chatStorage.currentChatId, streamingChat, newChat, debouncedSaveToHistory]);

  /**
   * 加载聊天状态标志
   */
  const isLoadingChatRef = useRef(false);

  /**
   * 加载历史聊天
   */
  const loadChat = useCallback((chatId: string) => {
    const chatData = chatStorage.loadChatFromLocal(chatId);
    if (!chatData) {
      return;
    }

    // 设置加载状态，防止在加载过程中触发自动保存
    isLoadingChatRef.current = true;

    // 重要：首先设置当前聊天ID，避免保存时创建新记录
    chatStorage.setCurrentChatId(chatId);

    // 提取conversationId - 从conversationState中获取
    let conversationId: string | null = null;
    
    // 从conversationState中获取conversation_id
    if (chatData.conversationState?.conversationId) {
      conversationId = chatData.conversationState.conversationId;
    }

    // 重置流式聊天状态，但保留conversationId
    streamingChat.newChat(conversationId);

    // 如果有conversationState，加载完整的对话状态
    if (chatData.conversationState) {
      streamingChat.loadConversation(conversationId, chatData.conversationState);
    }

    // 转换历史消息为流式聊天格式并添加
    chatData.messages.forEach(historyMsg => {
      const streamingMessage = {
        id: historyMsg.id,
        role: historyMsg.role,
        content: historyMsg.content,
        timestamp: historyMsg.timestamp,
        type: historyMsg.type,
        caseInfo: historyMsg.caseInfo,
        analysisCapability: historyMsg.analysisCapability,
        isStreaming: false,
        isStreamingComplete: historyMsg.isStreamingComplete,
        files: historyMsg.files,
        // 恢复策略选择相关字段
        strategies: historyMsg.strategies,
        currentStrategy: historyMsg.currentStrategy,
        canModify: historyMsg.canModify,
      };
      streamingChat.addMessage(streamingMessage);
      
      // 如果这是策略选择消息，恢复策略选择状态
      if (historyMsg.type === 'strategy_selection' && historyMsg.strategies) {
        // console.log('🔄 恢复策略选择状态:', historyMsg.strategies);
        // 这里需要手动设置策略选择状态，因为addMessage不会自动设置这些状态
        setTimeout(() => {
          streamingChat.setStrategySelectionState(
            historyMsg.strategies!,
            historyMsg.canModify || false,
            historyMsg.currentStrategy
          );
        }, 100);
      }
    });

    // 更新当前加载的聊天ID标记
    setCurrentLoadedChatId(chatId);

    // 加载完成，恢复自动保存
    setTimeout(() => {
      isLoadingChatRef.current = false;
    }, 100);

  }, [chatStorage, streamingChat]);

  /**
   * 删除聊天
   */
  const deleteChat = useCallback((chatId: string) => {
    chatStorage.deleteChatFromLocal(chatId);
    
    // 如果删除的是当前聊天，清空当前状态，但不自动创建新聊天
    if (chatStorage.currentChatId === chatId) {
      // 重置流式聊天状态
      streamingChat.newChat();
    }
  }, [chatStorage, streamingChat]);

  /**
   * 确认案件信息
   */
  const confirmCaseInfo = useCallback(async () => {
    await streamingChat.confirmCaseInfo();
    // 保存状态变更
    debouncedSaveToHistory();
  }, [streamingChat, debouncedSaveToHistory]);

  /**
   * 强制开始分析
   */
  const forceStartAnalysis = useCallback(async () => {
    await streamingChat.forceStartAnalysis();
    // 保存状态变更
    debouncedSaveToHistory();
  }, [streamingChat, debouncedSaveToHistory]);

  /**
   * 监听流式聊天消息变化，自动保存
   */
  useEffect(() => {
    if (streamingChat.messages.length > 0) {
      debouncedSaveToHistory();
    }
  }, [streamingChat.messages, debouncedSaveToHistory]);

  /**
   * 监听策略选择保存事件，立即保存
   */
  useEffect(() => {
    const handleStrategySelectionSaved = () => {
      // console.log('📥 接收到策略选择保存事件');
      // 立即保存，不等待防抖
      if (chatStorage.currentChatId && streamingChat.messages.length > 0) {
        // console.log('💾 开始保存策略选择到履历');
        const historyMessages = streamingChat.messages
          .filter(msg => msg) // 过滤掉null/undefined消息
          .map(convertStreamingMessageToHistory);
        
        // console.log('📝 转换后的历史消息:', historyMessages.length, '条');
        
        chatStorage.updateChatData(
          chatStorage.currentChatId,
          {
            messages: historyMessages,
            conversationState: {
              conversationId: streamingChat.conversation.conversationId,
              sessionId: streamingChat.conversation.conversationId,
              state: streamingChat.conversation.state,
              stage: 'free',
              caseInfo: streamingChat.conversation.caseInfo,
              completenessScore: streamingChat.conversation.completenessScore,
            }
          },
          true // 立即同步到服务器
        );
        // console.log('✅ 策略选择保存完成');
      } else {
        console.warn('⚠️ 无法保存策略选择: currentChatId或messages为空');
      }
    };

    const handlePhase3AnalysisComplete = () => {
      // 立即保存第三阶段分析结果
      if (chatStorage.currentChatId && streamingChat.messages.length > 0) {
        const historyMessages = streamingChat.messages
          .filter(msg => msg) // 过滤掉null/undefined消息
          .map(convertStreamingMessageToHistory);
        
        chatStorage.updateChatData(
          chatStorage.currentChatId,
          {
            messages: historyMessages,
            conversationState: {
              conversationId: streamingChat.conversation.conversationId,
              sessionId: streamingChat.conversation.conversationId,
              state: streamingChat.conversation.state,
              stage: 'phase3_complete',
              caseInfo: streamingChat.conversation.caseInfo,
              completenessScore: streamingChat.conversation.completenessScore,
            }
          },
          true // 立即同步到服务器
        );
      }
    };

    window.addEventListener('strategySelectionSaved', handleStrategySelectionSaved);
    window.addEventListener('phase3AnalysisComplete', handlePhase3AnalysisComplete);
    
    return () => {
      window.removeEventListener('strategySelectionSaved', handleStrategySelectionSaved);
      window.removeEventListener('phase3AnalysisComplete', handlePhase3AnalysisComplete);
    };
  }, [chatStorage, streamingChat, convertStreamingMessageToHistory]);

  /**
   * 使用ref来确保初始化只执行一次
   */
  const initializationRef = useRef(false);
  
  /**
   * 初始化聊天状态（不自动创建空聊天）
   */
  useEffect(() => {
    // 使用ref来确保只初始化一次，避免React严格模式的重复调用
    if (!initializationRef.current) {
      initializationRef.current = true;
      
      // 延迟执行，确保chatStorage已完全初始化
      setTimeout(() => {
        if (chatStorage.chatList.length > 0) {
          // 如果有聊天历史，自动加载最新的聊天
          if (!chatStorage.currentChatId) {
            const latestChat = chatStorage.chatList[0]; // 最新的聊天在列表首位
            if (latestChat) {
              // console.log('自动加载最新聊天:', latestChat.id);
              loadChat(latestChat.id);
            }
          }
        } else {
          // 没有聊天历史时，不自动创建聊天，等待用户发送消息时再创建
          // console.log('没有聊天历史，等待用户操作');
        }
      }, 100); // 100ms延迟确保状态同步
    }
  }, []); // 空依赖数组，只在组件挂载时执行一次

  // 额外的安全措施：定期检查并重置异常的加载状态
  useEffect(() => {
    const checkInterval = setInterval(() => {
      // 如果没有消息但是isLoading为true，并且没有正在进行的加载操作，则重置状态
      if (streamingChat.isLoading && streamingChat.messages.length === 0 && !isLoadingChatRef.current) {
        streamingChat.resetLoadingState();
      }
    }, 5000); // 每5秒检查一次

    return () => clearInterval(checkInterval);
  }, [streamingChat]);

  /**
   * 清理定时器
   */
  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, []);

  // 组合状态
  const combinedState: EnhancedChatState = {
    // 当前聊天状态
    currentChatId: chatStorage.currentChatId,
    messages: streamingChat.messages
      .filter(msg => msg) // 过滤掉null/undefined消息
      .map(convertStreamingMessageToHistory),
    conversation: {
      conversationId: streamingChat.conversation.conversationId,
      sessionId: streamingChat.conversation.conversationId, // 使用conversationId作为sessionId
      state: streamingChat.conversation.state,
      stage: 'free', // 默认阶段，可以根据实际情况调整
      caseInfo: streamingChat.conversation.caseInfo,
      completenessScore: streamingChat.conversation.completenessScore,
    },
    
    // 聊天历史
    chatList: chatStorage.chatList,
    
    // 加载状态 - 智能合并：对话状态优先于存储状态
    isLoading: (() => {
      // 如果对话已经进入clarifying、confirmed或completed状态，只使用streamingChat.isLoading
      if (streamingChat.conversation.state === 'clarifying' || 
          streamingChat.conversation.state === 'confirmed' ||
          streamingChat.conversation.state === 'completed') {
        // console.log(`对话状态${streamingChat.conversation.state}，使用streamingChat.isLoading:`, streamingChat.isLoading);
        return streamingChat.isLoading;
      }
      // 其他情况使用OR逻辑
      const finalLoading = streamingChat.isLoading || chatStorage.isLoading;
      // console.log(`对话状态${streamingChat.conversation.state}，合并isLoading:`, {
      //   streamingChat: streamingChat.isLoading,
      //   chatStorage: chatStorage.isLoading,
      //   final: finalLoading
      // });
      return finalLoading;
    })(),
    isStreaming: streamingChat.isStreaming,
    isSyncing: chatStorage.isSyncing,
    
    // 其他状态
    currentStatus: streamingChat.currentStatus,
    // 仅显示真正的对话/分析错误；同步错误作为非致命信息单独暴露
    error: streamingChat.error,
    lastSyncTime: chatStorage.lastSyncTime,
    syncError: chatStorage.error,
  };

  return {
    // 状态
    ...combinedState,
    
    // 流式聊天操作
    sendMessage,
    confirmCaseInfo,
    forceStartAnalysis,
    clearError: streamingChat.clearError,
    
    // 三阶段分析相关
    useThreePhaseAnalysis: streamingChat.useThreePhaseAnalysis,
    currentPhase: streamingChat.currentPhase,
    currentPhaseName: streamingChat.currentPhaseName,
    phaseResults: streamingChat.phaseResults,
    toggleThreePhaseAnalysis: streamingChat.toggleThreePhaseAnalysis,
    
    // 策略选择相关
    handleStrategySelection: streamingChat.handleStrategySelection,
    handleModificationDecision: streamingChat.handleModificationDecision,
    handleReselectStrategy: streamingChat.handleReselectStrategy,
    
    // 状态重置
    resetLoadingState: streamingChat.resetLoadingState,
    
    // 聊天历史操作
    newChat,
    loadChat,
    deleteChat,
    loadChatHistoryFromServer: chatStorage.loadChatHistoryFromServer,
    
    // 存储工具
    generateChatId: chatStorage.generateChatId,
    generateChatTitle: chatStorage.generateChatTitle,
  };
}; 